import { useMemo, useRef } from 'react';
import { useClientConfig } from '../../provider';
import { sendToGTM } from '../services/gtm.service';
import { sendToGA4 } from '../services/ga4.service';
import { sendToGoogleAds } from '../services/ads.service';
import { sendToFacebook } from '../services/facebook.service';
import { sendToTikTok } from '../services/tiktok.service';
import { sendToPinterest } from '../services/pinterest.service';
import { isDevelopment, validateEventData, generateUniversalEventId, getTtclid, getGclid } from '../utils/tracking.utils';
import type { 
  AnalyticsTracker, 
  TrackingEventName, 
  TrackingEvent, 
  BeginCheckoutEvent, 
  AddShippingInfoEvent, 
  AddPaymentInfoEvent, 
  PurchaseEvent 
} from '../types/tracking.types';

export const useAnalytics = (): AnalyticsTracker => {
  const config = useClientConfig();
  const { gtm_ids, ga4_ids, ads_ids, facebook_pixels, tiktok_pixels, pinterest_tags, client_slug, storeId, nanoId } = config;
  

  
  // Controle de eventos já disparados para evitar duplicação
  const firedEventsRef = useRef<Set<string>>(new Set());

  const trackEvent = (eventName: TrackingEventName, event: TrackingEvent) => {

    // Validação básica dos dados
    if (!validateEventData(eventName, event)) {
      return;
    }

    // Cria uma chave única para o evento baseada no conteúdo (evita duplicatas)
    const eventKey = `${eventName}_${nanoId}_${event.ecommerce.value}_${event.ecommerce.items?.length || 0}`;
    
    // Eventos únicos por sessão (com tratamento especial para desenvolvimento)
    if (['begin_checkout', 'purchase'].includes(eventName)) {
      const isDevMode = isDevelopment();
      const hasBeenFired = firedEventsRef.current.has(eventKey);
      
      // Em desenvolvimento, permite até 2 disparos de begin_checkout (para Hot Reload)
      // Em produção, só permite 1 disparo
      if (eventName === 'begin_checkout' && isDevMode) {
        const beginCheckoutCount = Array.from(firedEventsRef.current).filter(key => key.startsWith('begin_checkout')).length;
        if (beginCheckoutCount >= 2) {
          return;
        }
      } else if (hasBeenFired) {
        return;
      }
      
      firedEventsRef.current.add(eventKey);
    }

    // Purchase: event_id estável por pedido — alinha browser, Events API e webhook paid (dedup TikTok).
    const purchaseOrderId = eventName === 'purchase' ? String((event as any)?.orderId ?? '').trim() : '';
    const universalEventId =
      purchaseOrderId && purchaseOrderId !== 'NaN'
        ? `${purchaseOrderId}:purchase`
        : generateUniversalEventId(nanoId, eventName);

    // Adiciona metadados do cliente se não estiverem presentes
    event.client_slug ??= client_slug || storeId;
    event.storeId ??= storeId;

    // Corrige dados do ecommerce para todos os serviços
    if (event.ecommerce) {
      // Fixa valor com 2 decimais
      event.ecommerce.value = Number(event.ecommerce.value.toFixed(2));
      
      // Remove duplicatas dos content_ids se existir
      if (event.ecommerce.items) {
        event.ecommerce.items = event.ecommerce.items.map(item => ({
          ...item,
          item_id: item.item_id
        }));
      }
    }

    // Corrige dados do Facebook se existir
    if ((event as any).fb_data) {
      (event as any).fb_data.value = Number((event as any).fb_data.value.toFixed(2));
      if ((event as any).fb_data.content_ids) {
        (event as any).fb_data.content_ids = [...new Set((event as any).fb_data.content_ids)];
      }
    }

    // Envia também para o backend (Events API / deduplicação)
    // Não bloquear o fluxo do checkout se falhar.
    try {
      // MVP: só faz sentido se TikTok estiver configurado no client (senão evita ruído)
      if (tiktok_pixels.length > 0 && import.meta.env.VITE_BACKEND_URL) {
        const url = `${import.meta.env.VITE_BACKEND_URL}/tracking/events`;
        const payload = {
          platform: 'tiktok',
          event: eventName,
          event_id: universalEventId,
          offerNanoId: nanoId,
          storeId,
          orderId: (event as any)?.orderId ? String((event as any).orderId) : undefined,
          ecommerce: event.ecommerce,
          page: { url: window.location.href, referrer: document.referrer },
          client: { user_agent: navigator.userAgent },
          ttclid: getTtclid(),
          gclid: getGclid(),
        };

        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }).catch(() => undefined);
      }
    } catch {
      // ignore
    }

    
    gtm_ids.forEach(gtmId => sendToGTM(gtmId, event));
    ga4_ids.forEach(ga4Id => sendToGA4(ga4Id, eventName, event, universalEventId));
    facebook_pixels.forEach(pixelId => sendToFacebook(pixelId, eventName, event, universalEventId));
    tiktok_pixels.forEach(pixelCode => sendToTikTok(pixelCode, eventName, event, universalEventId));
    // Pinterest Tag é global: um track() envia para todos tags carregados
    if (pinterest_tags.length > 0) {
      sendToPinterest(pinterest_tags[0], eventName, event, universalEventId);
    }

    // Google Ads apenas para eventos de compra
    if (eventName === 'purchase') {
      ads_ids.forEach(adsId => sendToGoogleAds(adsId, event as PurchaseEvent, universalEventId));
    }
  };

  return useMemo(() => ({
    trackBeginCheckout: (data: BeginCheckoutEvent) => trackEvent(data.event ?? 'begin_checkout', data),
    trackAddShippingInfo: (data: AddShippingInfoEvent) => trackEvent(data.event ?? 'add_shipping_info', data),
    trackAddPaymentInfo: (data: AddPaymentInfoEvent) => trackEvent(data.event ?? 'add_payment_info', data),
    trackPurchase: (data: PurchaseEvent) => trackEvent(data.event ?? 'purchase', data),

    // PageView para Facebook, TikTok e Pinterest
    trackPageView() {
      // Opção A: PageView é disparado no init de cada provider
      // (Facebook/TikTok/Pinterest). Mantemos essa função como no-op
      // para evitar duplicação caso algum componente ainda a chame.
    },
  }), [gtm_ids, ga4_ids, ads_ids, facebook_pixels, tiktok_pixels, pinterest_tags, client_slug, storeId, nanoId]);
}; 